import type { MailboxInfo } from 'owa-client-types';
import { getConfig } from '../config';
import { getCloudEnvironmentForMailboxInfo } from 'owa-account-cloud';
import { logStartGreyError } from 'owa-analytics-start';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';

/**
 * Builds an absolute URL with the correct Outlook Web origin for the cloud environment of the target MailboxInfo.
 * @param mailboxInfo The mailbox information to use for determining the cloud environment; fallback behavior is: module context mailbox else GSA
 * @param url A relative or absolute URL intended to target Outlook Web
 * @returns A URL scoped within the appropriate Outlook Web origin for the target cloud environment.
 */
export async function getSovereignSafeOutlookUrl(
    mailboxInfo: MailboxInfo | undefined,
    url: RequestInfo
): Promise<RequestInfo> {
    if (!isBootFeatureEnabled('acct-sovereignCloudInfra')) {
        return url;
    }

    let mailboxInfoResult: MailboxInfoResult | undefined = undefined;
    try {
        mailboxInfoResult = await getMailboxInfo(mailboxInfo);
        if (!mailboxInfoResult.mailboxInfo) {
            throw new Error('No mailboxInfo to determine cloud environment');
        }

        /**
         * We only override the URL if the mailboxInfo indicates that cloud-aware routing is required.
         *
         * For OWA, requireCloudAwareRouting will never be true because only the native accounts infra will stamp this property.
         *   - OWA works without rerouting because it is served from the cloud to which it sends requests.
         * For Monarch, requireCloudAwareRouting will only be true if account setup determined that the mailbox was in a sovereign cloud.
         *   - Monarch requires this sovereign-specific rerouting because it is always served from Global cloud.
         */
        if (mailboxInfoResult.mailboxInfo.requireCloudAwareRouting) {
            const cloudEnv = await getCloudEnvironmentForMailboxInfo(mailboxInfoResult.mailboxInfo);
            url = cloudEnv.buildOutlookUrl(url);
        }

        return url;
    } catch (error) {
        // Errors resulting from enabling useUnknownInCatchVariables
        logStartGreyError(
            'GetSovereignSafeOutlookUrlFailed',
            mailboxInfoResult?.mailboxInfo,
            error instanceof Error ? error : undefined,
            {
                mailboxInfoSource: mailboxInfoResult?.source,
            }
        );
        return url;
    }
}

type MailboxInfoResult = {
    mailboxInfo: MailboxInfo | undefined;
    source?: 'param' | 'moduleContext' | 'globalSettings';
};

async function getMailboxInfo(requestMailboxInfo?: MailboxInfo): Promise<MailboxInfoResult> {
    if (requestMailboxInfo) {
        return { mailboxInfo: requestMailboxInfo, source: 'param' };
    }

    const moduleContextMailboxInfo = await getConfig().getModuleContextMailboxInfo?.();
    if (moduleContextMailboxInfo) {
        return { mailboxInfo: moduleContextMailboxInfo, source: 'moduleContext' };
    }

    const globalSettingsMailboxInfo = await getConfig().getGlobalSettingsAccountMailboxInfo?.();
    if (globalSettingsMailboxInfo) {
        return { mailboxInfo: globalSettingsMailboxInfo, source: 'globalSettings' };
    }

    return { mailboxInfo: undefined };
}
